import { useEffect, useState } from 'react';

import { DEFAULT_ARTICLE_LIMIT, PLACE_KIND, fetchJurisdictionNews } from '../utils/jurisdictionNewsApi';

/**
 * Archived news from one city's own government website, via api2.
 *
 * Distinct from useLocalNews, which is a web search for news ABOUT a city
 * from any publisher. This is the record of what the city government itself
 * published on its own site, which is a different claim and a different level
 * of trust. Named for what it returns rather than for the topic, so the two
 * cannot be confused at an import statement.
 *
 * Two requests in sequence inside fetchJurisdictionNews -- a name has to be resolved
 * to a GEOID before anything can be read -- but one loading state here, because
 * there is nothing to show between them.
 *
 * `jurisdiction` is returned even when there are no articles, and that is the
 * point of returning it. Three empty screens look identical and mean different
 * things:
 *
 *   jurisdiction null, no error   the archive has no such place
 *   jurisdiction set, 0 articles  known, not yet crawled
 *   error                         the request failed
 *
 * @param {Object} params
 * @param {string} params.city      - City name as the user picked it.
 * @param {string} params.stateAbbr - Two letter code. Required with city.
 * @param {string} [params.kind]    - 'place' (default) or 'county'.
 * @param {number} [params.limit]
 */
const useJurisdictionArticles = ({
  city,
  stateAbbr,
  kind = PLACE_KIND,
  limit = DEFAULT_ARTICLE_LIMIT,
} = {}) => {
  const [jurisdiction, setJurisdiction] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!city || !stateAbbr) {
      setJurisdiction(null);
      setArticles([]);
      setError(null);
      setLoading(false);
      return undefined;
    }

    const controller = new AbortController();

    const load = async () => {
      try {
        setLoading(true);
        setError(null);

        const result = await fetchJurisdictionNews(
          { city, stateAbbr, kind, limit },
          { signal: controller.signal }
        );

        if (controller.signal.aborted) return;

        setJurisdiction(result.jurisdiction);
        setArticles(result.articles);
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.error('[useJurisdictionArticles] failed', {
          city,
          stateAbbr,
          kind,
          status: err.status,
          message: err.message,
        });
        setJurisdiction(null);
        setArticles([]);
        setError(err);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    load();
    return () => controller.abort();
  }, [city, stateAbbr, kind, limit]);

  return { jurisdiction, articles, loading, error };
};

export default useJurisdictionArticles;